import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceTemplateStatus,
  IExperienceTemplate,
  ISupportingActor,
  SupportingActorBySubtask,
  SupportingActorPropagationScope,
  SupportingActorSlackChannel,
  SupportingActorStaticUser,
  SupportingActorType,
  SupportingActorUserProfile
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  Dialog,
  DialogContent,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { TFunction } from 'i18next';
import { camelCase } from 'lodash';
import { Archive, Lock, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { InfoBox } from '../../../../../../components/InfoBox';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import {
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../../../services/currentUser';
import { hasCurrentUserExperienceLevelScopes } from '../../../../../../services/permissions';
import {
  ActorUsageRef,
  findActorKeyCollision,
  findActorUsages,
  isReservedActorKey
} from '../../../../../../services/experiences/actorUsage';
import { NewUxButton } from '../../../../components/Builders/newux/kit/Button';
import { ClassicBlue600 } from '../../../../components/Builders/TaskBuilder/components/classicBlue600';
import {
  ActorColorField,
  HalfWidthField,
  ScopeSaveButton,
  useActorPickerColors
} from '../ActorDialogParts';
import { getActorEditRules } from '../actorEditRules';
import { channelActorType } from './actorTypes/channel';
import { profileActorType } from './actorTypes/profile';
import {
  RULE_BASED_USER_OPTION,
  ruleBasedActorType
} from './actorTypes/ruleBased';
import { samePersonActorType } from './actorTypes/samePerson';
import { subtaskActorType } from './actorTypes/subtask';

// The form's actor before it's saved: no key yet (it's derived from the name
// on save), no archived flag (archiving has its own buttons), and a profile
// actor's field may not be picked yet.
export type ProfileActorState = Omit<
  SupportingActorUserProfile,
  'key' | 'archived' | 'source' | 'field'
> & { field: string | null };
export type StaticUserActorState = Omit<
  SupportingActorStaticUser,
  'key' | 'archived'
> & {
  // Rule-based only: no default person when no option matches. Kept apart
  // from an empty defaultUserId, which also means "not picked yet".
  defaultUnassigned: boolean;
};
export type SubtaskActorState = Omit<
  SupportingActorBySubtask,
  'key' | 'archived'
>;
export type ChannelActorState = Omit<
  SupportingActorSlackChannel,
  'key' | 'archived'
>;
export type ActorState =
  | ProfileActorState
  | StaticUserActorState
  | SubtaskActorState
  | ChannelActorState;

// What carries over when the type changes.
export type ActorStateBase = Pick<
  ActorState,
  'name' | 'color' | 'ignoreWhenMissing'
>;

export type ActorTypeOption =
  | SupportingActorType
  | typeof RULE_BASED_USER_OPTION;

export type ActorTypeFieldsProps<S extends ActorState> = {
  state: S;
  onChange: (next: S) => void;
  disabled: boolean;
  // True once a save has been refused: the form then marks what's missing.
  showValidation: boolean;
  orgId: string;
  customFields: CustomUserField[];
  tpl: Doc<IExperienceTemplate>;
};

/** One choice of the dialog's type picker: its fields and how it saves. */
export type ActorTypeForm<S extends ActorState> = {
  option: ActorTypeOption;
  label: (t: TFunction) => string;
  // Whether an actor, or the form's state, is of this choice.
  matches: (actor: ActorState | ISupportingActor) => boolean;
  // The form's state for an existing actor this choice matches. An actor
  // without a colour gets the dialog's default one.
  fromActor: (actor: ISupportingActor, defaultColor: string) => S;
  // The form's state on switching to this choice.
  create: (base: ActorStateBase) => S;
  // Saving is refused until this holds.
  isComplete: (state: S) => boolean;
  toActor: (state: S, key: string) => ISupportingActor;
  // The fields below the colour / title / type row, if any.
  Fields?: React.ComponentType<ActorTypeFieldsProps<S>>;
};

/**
 * The type picker's choices, in the order it lists them. Each one only ever
 * gets the state it matches, so the list holds them as forms of any state.
 */
export const ACTOR_TYPE_FORMS = [
  profileActorType,
  samePersonActorType,
  subtaskActorType,
  ruleBasedActorType,
  channelActorType
] as unknown as ActorTypeForm<ActorState>[];

/** The choice an actor, or the form's state, belongs to. */
export const getActorTypeForm = (actor: ActorState | ISupportingActor) =>
  ACTOR_TYPE_FORMS.find((f) => f.matches(actor)) ?? ACTOR_TYPE_FORMS[0];

export const getActorTypeFormByOption = (option: ActorTypeOption) =>
  ACTOR_TYPE_FORMS.find((f) => f.option === option) ?? ACTOR_TYPE_FORMS[0];

// The dialog's fields and buttons in the new-UX blue, as the task modal's.
const ActorDialogRoot = ClassicBlue600.withComponent(Dialog);

const getInitialState = (
  value: ISupportingActor | null,
  defaultColor: string
): ActorState =>
  value
    ? getActorTypeForm(value).fromActor(value, defaultColor)
    : profileActorType.create({
        name: '',
        color: defaultColor,
        ignoreWhenMissing: false
      });

const ActorDialog = ({
  open,
  onClose,
  actors,
  customFields,
  value,
  onChange: onChangeActor,
  onRemove,
  onArchive,
  onUnarchive,
  title,
  buttonLabel,
  orgId,
  tpl
}: {
  open: boolean;
  onClose: () => void;
  actors: ISupportingActor[];
  customFields: CustomUserField[];
  value: ISupportingActor | null;
  onChange: (
    nextActor: ISupportingActor,
    propagationScope?: SupportingActorPropagationScope
  ) => void;
  onRemove?: () => void;
  onArchive?: (key: string) => void;
  onUnarchive?: (key: string) => void;
  title: React.ReactNode;
  buttonLabel: React.ReactNode;
  orgId: string;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const currentUser = useCurrentUser();
  const isDadoEmployee = useIsCurrentUserDadoEmployee();
  const canEditTemplate = hasCurrentUserExperienceLevelScopes(
    currentUser,
    tpl.id,
    ['edit']
  );
  // The first palette colour no other actor uses yet.
  const pickerColors = useActorPickerColors();
  const defaultColor = useMemo(() => {
    const used = new Set(actors.map((a) => a.color?.toLowerCase()));
    return (
      pickerColors.find((c) => !used.has(c.toLowerCase())) ||
      pickerColors[actors.length % pickerColors.length]
    );
  }, [actors, pickerColors]);
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<ActorState>(
    getInitialState(value, defaultColor)
  );

  const candidateKey = camelCase(state.name);
  const collidingActor = findActorKeyCollision(
    actors,
    candidateKey,
    value?.key
  );
  // A name of only whitespace/symbols (e.g. "!!!") camelCases to '' — treat
  // that as a conflict too, rather than silently saving key: ''. Gated on
  // state.name being non-empty so a freshly opened, untouched dialog
  // doesn't show this error before the user has typed anything.
  const hasEmptyCandidateKey = !!state.name && !candidateKey;
  const hasKeyConflict =
    hasEmptyCandidateKey ||
    !!collidingActor ||
    isReservedActorKey(candidateKey);
  const collidingArchivedActor = collidingActor?.archived
    ? collidingActor
    : null;

  const handleUnarchiveKeyCollision = () => {
    if (!collidingArchivedActor || !onUnarchive) return;
    onUnarchive(collidingArchivedActor.key);
    onClose();
  };

  const nameFieldHelperText = hasEmptyCandidateKey ? (
    t('Please use a name with at least one letter or number')
  ) : collidingArchivedActor ? (
    onUnarchive ? (
      <>
        {t('An archived actor with this name already exists.')}{' '}
        <Link
          component="button"
          type="button"
          variant="inherit"
          style={{ font: 'inherit', verticalAlign: 'baseline' }}
          onClick={handleUnarchiveKeyCollision}
        >
          {t('Unarchive it')}
        </Link>
      </>
    ) : (
      t('An archived actor with this name already exists.')
    )
  ) : hasKeyConflict ? (
    t('An actor with this name already exists')
  ) : undefined;

  // Archive/Unarchive are independent of disableEditing — they must stay
  // available even when field-editing is locked on a launched template.
  const isUnlaunchedTemplate =
    tpl.data.status === ExperienceTemplateStatus.ROUGH_DRAFT ||
    tpl.data.status === ExperienceTemplateStatus.DRAFT;
  const {
    disableEditing,
    disableRename,
    canRemove,
    propagates,
    asksPropagationScope,
    isBlockedTypeChange
  } = getActorEditRules({
    status: tpl.data.status,
    existingActorType: value?.type ?? null,
    isDadoEmployee,
    canEditTemplate
  });
  const [propagationScope, setPropagationScope] = useState(
    SupportingActorPropagationScope.ACTIVE_AND_NEW_INSTANCES
  );
  // Checked on save, so the type can still be picked and the refusal explained.
  const isBlockedChannelTypeChange =
    state.type === SupportingActorType.CHANNEL &&
    isBlockedTypeChange(SupportingActorType.CHANNEL);
  const [showChannelTypeError, setShowChannelTypeError] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const isActorArchived = !!value?.archived;
  const showArchiveAction =
    value !== null && !!onArchive && !isUnlaunchedTemplate && !isActorArchived;
  const showUnarchiveAction =
    value !== null && !!onUnarchive && !isUnlaunchedTemplate && isActorArchived;

  const [blockedAction, setBlockedAction] = useState<{
    type: 'archive' | 'remove';
    usages: ActorUsageRef[];
  } | null>(null);

  const handleRemove = () => {
    if (!value || !onRemove) return;
    const usages = findActorUsages(tpl.data, value.key);
    if (usages.length) {
      setBlockedAction({ type: 'remove', usages });
      return;
    }
    onRemove();
    onClose();
  };

  const handleArchive = () => {
    if (!value || !onArchive) return;
    const usages = findActorUsages(tpl.data, value.key);
    if (usages.length) {
      setBlockedAction({ type: 'archive', usages });
      return;
    }
    onArchive(value.key);
    onClose();
  };

  const handleUnarchive = () => {
    if (!value || !onUnarchive) return;
    onUnarchive(value.key);
    onClose();
  };

  useEffect(() => {
    setState(getInitialState(value, defaultColor));
    setBlockedAction(null);
    setPropagationScope(
      SupportingActorPropagationScope.ACTIVE_AND_NEW_INSTANCES
    );
    setShowChannelTypeError(false);
    setShowValidation(false);
  }, [value, defaultColor]);

  // The type picker's current choice: its fields, and how it saves.
  const typeForm = getActorTypeForm(state);
  const TypeFields = typeForm.Fields;

  const onSupportingActorTypeChange = (option: ActorTypeOption) => {
    setShowChannelTypeError(false);
    setShowValidation(false);
    setState((x) =>
      getActorTypeFormByOption(option).create({
        name: x.name,
        color: x.color,
        ignoreWhenMissing: x.ignoreWhenMissing
      })
    );
  };

  const handleSave = () => {
    if (isBlockedChannelTypeChange) {
      setShowChannelTypeError(true);
      return;
    }
    if (!typeForm.isComplete(state)) {
      setShowValidation(true);
      return;
    }
    onChangeActor(
      typeForm.toActor(state, camelCase(state.name)),
      propagates
        ? asksPropagationScope
          ? propagationScope
          : SupportingActorPropagationScope.ACTIVE_AND_NEW_INSTANCES
        : undefined
    );
    onClose();
  };

  const isTitleLocked = disableEditing || disableRename;
  const cannotSave = !state.name || !state.color || hasKeyConflict;
  // Left clickable for a blocked channel change, so that saving can explain
  // why it's refused.
  const disableSave = cannotSave && !isBlockedChannelTypeChange;

  return (
    <ActorDialogRoot open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleWithTypography>{title}</DialogTitleWithTypography>
      <DialogContent>
        <FlexContainerVertical fullWidth spacing={4}>
          {value === null && (
            <Typography variant="body1">
              {t(
                'Supporting actors are people inside the experience besides the participant who can be assigned tasks and receive messages.'
              )}
            </Typography>
          )}
          <FlexContainer fullWidth alignItems="flex-start" spacing={2}>
            <HalfWidthField>
              <FlexContainer fullWidth alignItems="flex-start" spacing={2}>
                <CustomInputLabel label={t('Color')}>
                  <ActorColorField
                    value={state.color}
                    disabled={disableEditing}
                    onChange={(nextColor) =>
                      setState((s) => ({ ...s, color: nextColor }))
                    }
                  />
                </CustomInputLabel>

                <CustomInputLabel
                  label={t('Title')}
                  subLabel={
                    isTitleLocked ? undefined : t('e.g. manager, buddy, IT')
                  }
                  fullWidth
                >
                  {isTitleLocked ? (
                    <Tooltip
                      title={t(
                        'Title cannot be changed after the experience has been launched'
                      )}
                    >
                      <TextField
                        variant="outlined"
                        fullWidth
                        disabled
                        value={state.name}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock size={16} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={state.name}
                      onChange={(ev) =>
                        setState((s) => ({ ...s, name: ev.target.value }))
                      }
                      placeholder={t('Manager')}
                      error={hasKeyConflict}
                      helperText={nameFieldHelperText}
                    />
                  )}
                </CustomInputLabel>
              </FlexContainer>
            </HalfWidthField>

            <HalfWidthField>
              <CustomInputLabel label={t('Supporting actor type')} fullWidth>
                <Select
                  variant="outlined"
                  fullWidth
                  value={typeForm.option}
                  disabled={disableEditing}
                  error={showChannelTypeError && isBlockedChannelTypeChange}
                  onChange={(ev) =>
                    onSupportingActorTypeChange(
                      ev.target.value as ActorTypeOption
                    )
                  }
                >
                  {ACTOR_TYPE_FORMS.map((f) => (
                    <MenuItem key={f.option} value={f.option}>
                      {f.label(t)}
                    </MenuItem>
                  ))}
                </Select>
              </CustomInputLabel>
            </HalfWidthField>
          </FlexContainer>

          {showChannelTypeError && isBlockedChannelTypeChange && (
            <InfoBox color="red" fullWidth>
              {t(
                'This experience has already launched, so this actor can’t be changed to a Slack channel. Choose another type, or add a new Slack channel actor instead.'
              )}
            </InfoBox>
          )}

          {TypeFields && (
            <TypeFields
              state={state}
              onChange={setState}
              disabled={disableEditing}
              showValidation={showValidation}
              orgId={orgId}
              customFields={customFields}
              tpl={tpl}
            />
          )}

          {!!blockedAction && (
            <InfoBox color="red" fullWidth>
              <div>
                {/* nsSeparator: false — a ':' in the string is i18next's default
                    namespace separator, so a raw colon here would otherwise
                    silently break the lookup and render as an empty string. */}
                {blockedAction.type === 'archive'
                  ? t(
                      "This actor cannot be archived, because it's still being used in the following places:",
                      { nsSeparator: false }
                    )
                  : t(
                      "This actor cannot be removed, because it's still being used in the following places:",
                      { nsSeparator: false }
                    )}
              </div>
              <ul>
                {blockedAction.usages.map((usage, i) => (
                  <li key={i}>
                    {usage.itemTitle} ({usage.detail})
                  </li>
                ))}
              </ul>
              <div>
                {blockedAction.type === 'archive'
                  ? t(
                      'Please update these places in the experience to remove references to this actor, then you can archive it.'
                    )
                  : t(
                      'Please update these places in the experience to remove references to this actor, then you can remove the actor.'
                    )}
              </div>
            </InfoBox>
          )}
        </FlexContainerVertical>
      </DialogContent>
      <DialogActionsWithSlots
        left={
          <>
            {onRemove && canRemove && (
              <NewUxButton
                variant="outlined"
                color="secondary"
                startIcon={<Trash2 size={16} />}
                onClick={handleRemove}
              >
                {t('Remove')}
              </NewUxButton>
            )}
            {showUnarchiveAction && (
              <NewUxButton
                variant="outlined"
                color="primary"
                startIcon={<Archive size={16} />}
                onClick={handleUnarchive}
              >
                {t('Unarchive')}
              </NewUxButton>
            )}
            {showArchiveAction && (
              <NewUxButton
                variant="outlined"
                color="secondary"
                startIcon={<Archive size={16} />}
                onClick={handleArchive}
              >
                {t('Archive')}
              </NewUxButton>
            )}
          </>
        }
        right={
          disableEditing ? (
            <NewUxButton variant="text" color="primary" onClick={onClose}>
              {t('Close')}
            </NewUxButton>
          ) : (
            <>
              <NewUxButton variant="text" color="primary" onClick={onClose}>
                {t('Cancel')}
              </NewUxButton>
              {asksPropagationScope ? (
                <ScopeSaveButton
                  scope={propagationScope}
                  onChangeScope={setPropagationScope}
                  onSave={handleSave}
                  disabled={disableSave}
                />
              ) : (
                <NewUxButton
                  variant="contained"
                  color="primary"
                  disabled={disableSave}
                  onClick={handleSave}
                >
                  {propagates
                    ? t('Save for all active experiences')
                    : buttonLabel}
                </NewUxButton>
              )}
            </>
          )
        }
      />
    </ActorDialogRoot>
  );
};

export const AddActorDialog = ({
  open,
  onClose,
  actors,
  customFields,
  onAdd,
  onUnarchive,
  orgId,
  tpl
}: {
  open: boolean;
  onClose: () => void;
  actors: ISupportingActor[];
  customFields: CustomUserField[];
  onAdd: (nextActor: ISupportingActor) => void;
  onUnarchive?: (key: string) => void;
  orgId: string;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <ActorDialog
      open={open}
      onClose={onClose}
      actors={actors}
      customFields={customFields}
      value={null}
      onChange={onAdd}
      onUnarchive={onUnarchive}
      title={t('Add supporting actor')}
      buttonLabel={t('Add supporting actor')}
      orgId={orgId}
      tpl={tpl}
    />
  );
};

export const EditActorDialog = ({
  open,
  onClose,
  actors,
  customFields,
  value,
  onChange,
  onRemove,
  onArchive,
  onUnarchive,
  orgId,
  tpl
}: {
  open: boolean;
  onClose: () => void;
  actors: ISupportingActor[];
  customFields: CustomUserField[];
  value: ISupportingActor;
  onChange: (
    nextActor: ISupportingActor,
    propagationScope?: SupportingActorPropagationScope
  ) => void;
  onRemove?: () => void;
  onArchive?: (key: string) => void;
  onUnarchive?: (key: string) => void;
  orgId: string;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <ActorDialog
      open={open}
      onClose={onClose}
      actors={actors}
      customFields={customFields}
      value={value}
      onChange={onChange}
      onRemove={onRemove}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      title={t('Edit supporting actor')}
      buttonLabel={t('Save changes')}
      orgId={orgId}
      tpl={tpl}
    />
  );
};
